import { format, isValid, parse, parseISO } from 'date-fns';

import type {
  PlayerProgressBadge,
  PlayerProgressCheckpoint,
  PlayerProgressItem,
} from '@/features/player/components/types';

export const CHECKPOINT_LEADER_DOTS = '.'.repeat(300);

/** Matches iOS `Status.completed` for checkpoint rows (string values from Firestore / API). */
export function isCheckpointStatusCompleted(status: string | undefined | null): boolean {
  const s = String(status ?? '')
    .trim()
    .toLowerCase();
  return s === 'completed' || s === 'complete';
}

/**
 * Sort checkpoints in the same order as iOS screenshots:
 * - Group by prefix when name contains '.' or ':' (e.g. "sand.forms")
 * - Within a group: Forms -> Skill 1 -> Skill 2
 * - Root-only items are exactly Forms / Skill 1 / Skill 2 (no delimiter)
 * - Standalone items like "Etiquette" / "Score 1" keep their own ordering bucket
 * - Two-part keys like `knowledge.equipment` or `level6.etiquette` sort by the topic segment
 *   (the shared prefix is not in `groupOrder`, so without this they would keep API order).
 */
export function sortProgressCheckpoints(
  checkpoints: PlayerProgressCheckpoint[] | undefined | null,
): PlayerProgressCheckpoint[] {
  const list = Array.isArray(checkpoints) ? checkpoints : [];

  const normalizeToken = (raw: unknown) =>
    String(raw ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');

  const parseCheckpointKey = (rawName: unknown) => {
    const raw = String(rawName ?? '')
      .trim()
      .toLowerCase();
    if (!raw) return { group: '', suffix: '', hasDelimiter: false, depth: 0 };

    // Prefer splitting on '.' (Firestore-style), fallback to ':' (display-style).
    const hasDelimiter = raw.includes('.') || raw.includes(':');
    const parts = raw.includes('.') ? raw.split('.') : raw.split(':');
    const group = normalizeToken(parts[0]);
    const suffix = normalizeToken(parts.slice(1).join('.'));

    return { group, suffix, hasDelimiter, depth: parts.length };
  };

  const isKnowledgeNamespacePrefix = (group: string) =>
    group === 'knowledge' || /^level\d+$/.test(group);

  /** When Firestore uses `knowledge.<topic>` / `level6.<topic>`, rank by `<topic>` only (exactly two segments). */
  const groupForSort = (key: {
    group: string;
    suffix: string;
    hasDelimiter: boolean;
    depth: number;
  }) => {
    if (
      key.hasDelimiter &&
      key.depth === 2 &&
      key.suffix &&
      isKnowledgeNamespacePrefix(key.group)
    ) {
      return key.suffix;
    }
    return key.group;
  };

  const suffixRank = (suffix: string) => {
    if (suffix.includes('forms') || suffix === 'form') return 0;
    if (suffix.includes('skill1')) return 1;
    if (suffix.includes('skill2')) return 2;
    return 99;
  };

  const groupOrder = [
    'pitching',
    'chipping',
    'sand',
    'drivershot',
    'ironshot',
    // level 1–6 knowledge: etiquette → equipment → rules → terminology
    'etiquette',
    'equipment',
    'rules',
    'terminology',
    // level 7–9 knowledge: equipment (above) → mental → rules & etiquette → fitness
    // (`mental` is Firestore-style shorthand; same slot as mentalapproach)
    'mentalapproach',
    'rulesandetiquette',
    'fitness',
    // end
    'score1',
    'score2',
  ];

  const canonicalGroupForOrder = (group: string) => (group === 'mental' ? 'mentalapproach' : group);

  const groupRank = (group: string) => {
    if (group === '__root__') return -1;
    const idx = groupOrder.indexOf(canonicalGroupForOrder(group));
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };

  const rootSuffixFromName = (rawName: unknown) => {
    const n = normalizeToken(rawName);
    if (n === 'forms') return 'forms';
    if (n === 'skill1') return 'skill1';
    if (n === 'skill2') return 'skill2';
    return '';
  };

  return list
    .map((cp, i) => ({ cp, i }))
    .sort((a, b) => {
      const aKey = parseCheckpointKey(a.cp?.name);
      const bKey = parseCheckpointKey(b.cp?.name);

      const aRootSuffix = !aKey.hasDelimiter ? rootSuffixFromName(a.cp?.name) : '';
      const bRootSuffix = !bKey.hasDelimiter ? rootSuffixFromName(b.cp?.name) : '';
      const aIsRoot = Boolean(aRootSuffix);
      const bIsRoot = Boolean(bRootSuffix);

      const aGroup = aIsRoot ? '__root__' : groupForSort(aKey);
      const bGroup = bIsRoot ? '__root__' : groupForSort(bKey);

      const aGroupRank = groupRank(aGroup);
      const bGroupRank = groupRank(bGroup);
      if (aGroupRank !== bGroupRank) return aGroupRank - bGroupRank;

      const aSuffix = aKey.hasDelimiter ? aKey.suffix : aRootSuffix;
      const bSuffix = bKey.hasDelimiter ? bKey.suffix : bRootSuffix;
      const aSuffixRank = suffixRank(aSuffix);
      const bSuffixRank = suffixRank(bSuffix);
      if (aSuffixRank !== bSuffixRank) return aSuffixRank - bSuffixRank;

      return a.i - b.i;
    })
    .map(x => x.cp);
}

/** Display order for skill badges within a level (matches iOS). */
export const BADGE_ORDER = ['putting', 'aroundthegreen', 'fullswing', 'mastery', 'knowledge'];

/** Rank a badge by name against `BADGE_ORDER`; unknown names sort to the end. */
function badgeRank(name: string | undefined): number {
  const idx = BADGE_ORDER.indexOf(
    String(name ?? '')
      .trim()
      .toLowerCase(),
  );
  return idx === -1 ? BADGE_ORDER.length : idx;
}

/** Sort a badge list into `BADGE_ORDER` without mutating the input. */
export function sortProgressBadges(
  badges: PlayerProgressBadge[] | undefined | null,
): PlayerProgressBadge[] {
  return [...(badges ?? [])].sort((a, b) => badgeRank(a?.name) - badgeRank(b?.name));
}

/** Return a progress item with its badges sorted into `BADGE_ORDER`. */
export function sortProgressItemBadges(item: PlayerProgressItem): PlayerProgressItem {
  return { ...item, badges: sortProgressBadges(item.badges) };
}

export function parseLevelNumber(levelName: string | undefined): number | null {
  const raw = String(levelName ?? '').trim();
  if (!raw) return null;
  const match = raw.match(/(\d{1,2})/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
}

export function formatDisplayDate(raw: string | undefined): string | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;

  if (trimmed.includes('T')) {
    const iso = parseISO(trimmed);
    if (isValid(iso)) return format(iso, 'MMMM d, yyyy');
  }

  const normalized = trimmed.replace(/,(\d{4})$/, ', $1');
  const parsed = parse(normalized, 'EEEE, MMMM d, yyyy', new Date());
  if (isValid(parsed)) return format(parsed, 'MMMM d, yyyy');

  return trimmed;
}

/** Parse stored attempt / earned date strings to a Date (local interpretation). */
export function parseProgressDateStringToDate(raw: string | undefined | null): Date | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;

  if (trimmed.includes('T')) {
    const iso = parseISO(trimmed);
    return isValid(iso) ? iso : null;
  }

  const normalized = trimmed.replace(/,(\d{4})$/, ', $1');
  const legacy = parse(normalized, 'EEEE, MMMM d, yyyy', new Date());
  if (isValid(legacy)) return legacy;

  const slash = parse(trimmed, 'MM/dd/yyyy', new Date());
  if (isValid(slash)) return slash;

  const longMonth = parse(trimmed, 'MMMM d, yyyy', new Date());
  if (isValid(longMonth)) return longMonth;

  return null;
}

/**
 * Spec: multiple incomplete records on the same calendar day → one line; different days → separate.
 * Returns `MM/dd/yy` strings, newest day first.
 */
export function uniqueAttemptDisplayDatesNewestFirst(attempts: unknown[]): string[] {
  const byDay = new Map<string, Date>();
  for (const a of attempts) {
    const d = parseProgressDateStringToDate(String(a ?? ''));
    if (!d) continue;
    const dayKey = format(d, 'yyyy-MM-dd');
    const prev = byDay.get(dayKey);
    if (!prev || d.getTime() > prev.getTime()) {
      byDay.set(dayKey, d);
    }
  }
  return Array.from(byDay.values())
    .sort((a, b) => b.getTime() - a.getTime())
    .map(d => format(d, 'MM/dd/yy'));
}

/**
 * Current-level skill header: all checkpoints completed → "Completed on MM/dd/yy";
 * otherwise "Not Complete". Date prefers `badge.dateEarned`, else latest completion among checkpoints.
 */
export function getSkillHeaderStatusLabel(
  badge: PlayerProgressBadge,
  checkpoints: PlayerProgressCheckpoint[],
  completedOnLabel: string,
  notCompleteLabel: string,
): string {
  if (badge.badgeEarned) {
    const fromBadge = formatDateEarnedShort(badge.dateEarned);
    if (fromBadge) return `${completedOnLabel} ${fromBadge}`;
    return completedOnLabel;
  }
  if (checkpoints.length === 0) return notCompleteLabel;
  if (!checkpoints.every(cp => isCheckpointStatusCompleted(cp.status))) {
    return notCompleteLabel;
  }

  const fromBadge = formatDateEarnedShort(badge.dateEarned);
  if (fromBadge) return `${completedOnLabel} ${fromBadge}`;

  let maxD: Date | null = null;
  for (const cp of checkpoints) {
    const arr = Array.isArray(cp.attempts) ? cp.attempts : [];
    if (arr.length === 0) continue;
    const last = String(arr[arr.length - 1] ?? '');
    const d = parseProgressDateStringToDate(last);
    if (d && (!maxD || d.getTime() > maxD.getTime())) maxD = d;
  }
  if (maxD) return `${completedOnLabel} ${format(maxD, 'MM/dd/yy')}`;
  return completedOnLabel;
}

/** Badge earned date in completed levels: always `MM/dd/yy`. */
export function formatDateEarnedShort(raw: string | undefined): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';

  if (trimmed.includes('T')) {
    const iso = parseISO(trimmed);
    if (isValid(iso)) return format(iso, 'MM/dd/yy');
  }

  const normalized = trimmed.replace(/,(\d{4})$/, ', $1');
  const legacy = parse(normalized, 'EEEE, MMMM d, yyyy', new Date());
  if (isValid(legacy)) return format(legacy, 'MM/dd/yy');

  const slash = parse(trimmed, 'MM/dd/yyyy', new Date());
  if (isValid(slash)) return format(slash, 'MM/dd/yy');

  const longMonth = parse(trimmed, 'MMMM d, yyyy', new Date());
  if (isValid(longMonth)) return format(longMonth, 'MM/dd/yy');

  return trimmed;
}

export const MAX_PLAYER_LEVEL = 10;

export function parsePlayerProfileLevel(level: number | string | null | undefined): number {
  const n = typeof level === 'string' ? Number(level) : level;
  return typeof n === 'number' && Number.isFinite(n) && n >= 1 ? n : 1;
}

/** Clears all progress for one level (same rules as MutatePlayerLevelTab clear-data). */
export function clearPlayerLevelProgress(item: PlayerProgressItem): PlayerProgressItem {
  const nextBadges = (item.badges ?? []).map((badge: PlayerProgressBadge) => ({
    ...badge,
    badgeEarned: false,
    dateEarned: undefined,
    checkpoints: (badge.checkpoints ?? []).map((cp: PlayerProgressCheckpoint) => ({
      ...cp,
      status: 'unattempted',
      attempts: [] as unknown[],
    })),
  }));

  return {
    ...item,
    completed: false,
    dateCompleted: undefined,
    badges: nextBadges,
  };
}

/**
 * When current level is lowered, reset progress for every level from `newLevel`
 * through `maxLevel` (inclusive) back to unattempted.
 */
export function resetPlayerProgressFromLevelDowngrade(
  progress: PlayerProgressItem[],
  newLevel: number,
  maxLevel: number = MAX_PLAYER_LEVEL,
): PlayerProgressItem[] {
  return progress.map(item => {
    const levelNum = parseLevelNumber(item?.name);
    if (levelNum == null || levelNum < newLevel || levelNum > maxLevel) return item;
    return clearPlayerLevelProgress(item);
  });
}

/** Marks every checkpoint/badge for one level completed (inverse of clearPlayerLevelProgress). */
export function completePlayerLevelProgress(
  item: PlayerProgressItem,
  dateEarned: string,
): PlayerProgressItem {
  const nextBadges = (item.badges ?? []).map((badge: PlayerProgressBadge) => ({
    ...badge,
    badgeEarned: true,
    // Newly earned (badgeEarned was false): stamp `dateEarned` with the current date.
    // Already earned: keep the existing date.
    dateEarned: badge.badgeEarned ? (badge.dateEarned ?? dateEarned) : dateEarned,
    checkpoints: (badge.checkpoints ?? []).map((cp: PlayerProgressCheckpoint) => {
      // Already completed: keep attempts untouched so the existing completion date stands.
      if (isCheckpointStatusCompleted(cp.status)) return cp;
      // Newly completed: append `dateEarned` as the trailing attempt (the completion date).
      const attempts = Array.isArray(cp.attempts) ? cp.attempts : [];
      return {
        ...cp,
        status: 'completed',
        attempts: [...attempts, dateEarned],
      };
    }),
  }));

  return {
    ...item,
    completed: true,
    dateCompleted: item.dateCompleted ?? dateEarned,
    badges: nextBadges,
  };
}

/**
 * When current level is raised, mark every level below `newLevel` (1..newLevel-1)
 * as completed. Levels at/above `newLevel` are left untouched (current/future).
 * Existing earned dates are preserved; missing ones default to `dateEarned`.
 */
export function completePlayerProgressFromLevelUpgrade(
  progress: PlayerProgressItem[],
  newLevel: number,
  dateEarned: string = new Date().toISOString(),
): PlayerProgressItem[] {
  return progress.map(item => {
    const levelNum = parseLevelNumber(item?.name);
    if (levelNum == null || levelNum >= newLevel) return item;
    return completePlayerLevelProgress(item, dateEarned);
  });
}
